import { ApolloClient } from "apollo-client";
import * as Sentry from "@sentry/browser";
import {
  GET_CHECKOUT,
  CHECKOUT_COMPLETED,
  CREATE_CHECKOUT,
  UPDATE_CHECKOUT,
  COMPLETE_CHECKOUT,
  GET_CHECKOUT_WITH_PAYMENT,
} from "./queries";
import { createCheckout as createCheckoutType } from "../web-nextjs/__generated__/createCheckout";
import { checkoutCompleted } from "../web-nextjs/__generated__/checkoutCompleted";
import {
  getCheckout as getCheckoutType,
  getCheckout_checkout,
} from "../web-nextjs/__generated__/getCheckout";
import {
  updateCheckout as updateCheckoutType,
  updateCheckout_updateCheckout,
} from "../web-nextjs/__generated__/updateCheckout";
import { CheckoutUpdateInput } from "../web-nextjs/__generated__/globalTypes";
import {
  completeCheckout,
  completeCheckoutVariables,
} from "../web-nextjs/__generated__/completeCheckout";

export async function createCheckout(client: ApolloClient<{}>) {
  try {
    const {
      data: {
        createCheckout: { id },
      },
    } = await client.mutate<createCheckoutType>({
      mutation: CREATE_CHECKOUT,
      variables: {
        data: {},
      },
    });
    return id;
  } catch (error) {
    console.error(error);
    Sentry.captureException(error);
    return null;
  }
}
export async function getCheckout(
  client: ApolloClient<{}>,
  checkoutId: string
) {
  try {
    const {
      data: { checkout },
    }: {
      data: getCheckoutType;
    } = await client.query({
      query: GET_CHECKOUT,
      variables: {
        id: checkoutId,
      },
    });
    return checkout;
  } catch (error) {
    console.error(error);
    Sentry.captureException(error);
    return null;
  }
}

export async function getCompleted(
  client: ApolloClient<{}>,
  checkoutId: string
): Promise<boolean> {
  try {
    const {
      data: { checkout },
    }: {
      data: checkoutCompleted;
    } = await client.query({
      query: CHECKOUT_COMPLETED,
      variables: {
        id: checkoutId,
      },
    });
    return checkout.completed;
  } catch (error) {
    console.error(error);
    Sentry.captureException(error);
    return null;
  }
}

export async function updateCheckout(
  client: ApolloClient<{}>,
  checkoutId: string,
  paymentId: string,
  data: CheckoutUpdateInput,
  cb?: (err: string) => void
): Promise<updateCheckout_updateCheckout> {
  try {
    const {
      data: { updateCheckout },
      errors,
    } = await client.mutate<updateCheckoutType>({
      mutation: UPDATE_CHECKOUT,
      variables: {
        checkoutId,
        data,
      },
      errorPolicy: "all",
    });

    if (errors?.length > 0) {
      cb?.(errors?.[0]?.message);
      return null;
    }

    if (!paymentId) return updateCheckout;
    return await getCheckoutInfoWithPaymentId(client, checkoutId, paymentId);
  } catch (error) {
    console.error(error);
    Sentry.captureException(error);
    return null;
  }
}
export async function getCheckoutInfoWithPaymentId(
  client: ApolloClient<{}>,
  checkoutId: string,
  paymentId: string
): Promise<getCheckout_checkout> {
  try {
    const {
      data: { getTotalSumByPayment: checkout },
    }: {
      data: any;
    } = await client.query({
      query: GET_CHECKOUT_WITH_PAYMENT,
      variables: {
        checkout: { id: checkoutId },
        payment: { id: paymentId },
      },
      fetchPolicy: "network-only",
    });
    return checkout;
  } catch (error) {
    console.error(error);
    Sentry.captureException(error);
    return null;
  }
}
export async function completeCheckoutWithPayment(
  client: ApolloClient<{}>,
  checkoutId: string,
  paymentId: string
) {
  try {
    const {
      data: {
        completeCheckoutWithPayment: {
          checkout,
          payment: { url },
        },
      },
    } = await client.mutate<completeCheckout, completeCheckoutVariables>({
      mutation: COMPLETE_CHECKOUT,
      variables: {
        checkoutId,
        paymentId,
      },
    });
    return { checkout, url };
  } catch (error) {
    const shippingLineError =
      error.message.indexOf("ShippingLine in Checkout") >= 0;
    Sentry.captureException(error);
    return { shippingLineError };
  }
}
